import { useId } from "@floating-ui/react"
import classNames from "classnames"
import { xor } from "lodash-es"
import { useCallback } from "react"
import { Icon } from "../Icon"
import { useControlled } from "../utils/useControlled"
import {
  AccordionButtonProps,
  AccordionContentProps,
  AccordionItemProps,
  AccordionProps,
} from "./types"
import {
  useAccordionContext,
  AccordionItemContext,
  useAccordionItemContext,
  AccordionContext,
} from "./AccordionContext"
import { devWarning } from "../utils/devWarning"

function AccordionItem({ children, className, disabled, id: idProp, ...rest }: AccordionItemProps) {
  const { open } = useAccordionContext()
  const _id = useId()
  const id = idProp || _id
  const containerId = `accordion-item:${id}`
  const buttonId = `accordion-button:${id}`
  const labelId = `accordion-label:${id}`
  const descriptionId = `accordion-description:${id}`
  const isExpanded = open.includes(id)

  const _className = classNames(
    "flex flex-1 flex-col rounded-lg border border-gray-200 shadow-sm transition-[border-color]",
    {
      "border-gray-300": !disabled && isExpanded,
    },
    className,
  )

  return (
    <AccordionItemContext.Provider
      value={{
        buttonId,
        containerId,
        labelId,
        descriptionId,
        disabled,
        id,
        isExpanded,
      }}
    >
      <div className={_className} {...rest}>
        {children}
      </div>
    </AccordionItemContext.Provider>
  )
}

function AccordionButton({
  children,
  hint,
  startAdornment,
  endAdornment,
  className,
  ...rest
}: AccordionButtonProps) {
  const { toggleOpen } = useAccordionContext()
  const { buttonId, containerId, descriptionId, labelId, disabled, id, isExpanded } =
    useAccordionItemContext()

  const handleClick = useCallback(() => toggleOpen?.(id), [id, toggleOpen])

  const _classNameButton = classNames(
    "rounded-t-lg bg-gray-100 px-4 py-3 text-left text-gray-400 leading-none outline-none ring-primary-lighten-50 transition-all focus-visible:rounded-lg focus-visible:ring-4",
    {
      "rounded-lg": disabled || !isExpanded,
    },
    className,
  )

  const _classNameButtonContent = classNames({
    "opacity-60": disabled,
  })

  const _classNameIcon = classNames("transition-transform text-black", {
    "-rotate-180": !disabled && isExpanded,
  })

  const _classNameButtonText = classNames("font-medium leading-6", {
    "text-black": isExpanded,
    "place-self-start": hint,
  })

  const _classNameButtonRow = classNames("grid gap-x-2 items-center", {
    "grid-cols-[minmax(24px,auto)_minmax(60%,_1fr)_auto_32px]": startAdornment && endAdornment,
    "grid-cols-[minmax(24px,auto)_minmax(60%,_1fr)_32px]": startAdornment && !endAdornment,
    "grid-cols-[minmax(60%,_1fr)_auto_32px]": !startAdornment && endAdornment,
    "grid-cols-[minmax(60%,_1fr)_32px]": !startAdornment && !endAdornment,
  })

  const _classNameButtonHint = classNames("text-xs leading-4 -mt-2", {
    "col-start-2": startAdornment,
  })

  return (
    <button
      {...rest}
      aria-expanded={isExpanded}
      aria-controls={containerId}
      aria-labelledby={labelId}
      aria-describedby={hint && descriptionId}
      className={_classNameButton}
      disabled={disabled}
      id={buttonId}
      type="button"
      onClick={handleClick}
    >
      <div className={_classNameButtonContent}>
        <div className={_classNameButtonRow}>
          {startAdornment}
          <div className={_classNameButtonText} id={labelId}>
            {children}
          </div>
          {endAdornment}
          <Icon aria-hidden className={_classNameIcon} name="ChevronDown" size="md" />
          {hint && (
            <span className={_classNameButtonHint} id={descriptionId}>
              {hint}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

function AccordionContent({ children, className, ...rest }: AccordionContentProps) {
  const { containerId, labelId, disabled, isExpanded } = useAccordionItemContext()
  const _className = classNames(
    "grid transition-[grid-template-rows] duration-300 ease-in-out",
    {
      "grid-rows-[0fr]": disabled || !isExpanded,
      "grid-rows-[1fr]": !disabled && isExpanded,
    },
    className,
  )

  return (
    <div id={containerId} role="region" aria-labelledby={labelId} className={_className} {...rest}>
      <div className="overflow-hidden">
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

export function Accordion({
  allowMultiple,
  defaultOpen,
  open: openProp,
  children,
  className,
  onChange,
  ...rest
}: AccordionProps) {
  const [open, setControlledOpen] = useControlled({
    controlled: openProp,
    default: defaultOpen,
    name: "Accordion open",
  })
  const _className = classNames("space-y-4", className)

  devWarning(
    typeof defaultOpen !== "undefined" &&
      !(typeof openProp === "undefined" || typeof onChange === "undefined"),
    "Controlled accordion. The `defaultOpen` prop will be ignored when `open` or `onChange` props are provided.",
  )

  const toggleOpen = (idToToggle: string) => {
    let nextOpen: string[] = open?.includes(idToToggle) ? [] : [idToToggle]

    if (allowMultiple) {
      nextOpen = xor(open, [idToToggle])
    }

    setControlledOpen(nextOpen)
    onChange?.(nextOpen)
  }

  return (
    <AccordionContext.Provider value={{ toggleOpen, open: open || [] }}>
      <div className={_className} {...rest}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

Accordion.Item = AccordionItem
Accordion.Button = AccordionButton
Accordion.Content = AccordionContent
